import { useEffect, createRef, useState, ReactElement } from 'react'
import styled from 'styled-components'
import SVGSlider from './SVGSlider'
import HintAnimation from './HintAnimation'

import { calculateUsableDimensions } from '../utils'
import Placeholder from './Placeholder'

interface ImgContainerProps {
  image: string
  left: number
  uWidth: string
}

interface FrameProps {
  left: number
}

interface WrapperProps {
  uHeight: string
  uWidth: string
}

interface TryOnProps {
  data: any
  height: number
  width: number
  aspectRatio: number
  frameWidth: number
  slideFull: string
  frontOnly: boolean
}

const Wrapper = styled.div`
  position: relative;
  width: ${(props: WrapperProps) => props.uWidth};
  height: ${(props: WrapperProps) => props.uHeight};
  background-color: #ffffff;
  margin: 0 auto;
`

const ImgContainer = styled.div`
  background-image: url(${(props: ImgContainerProps) => props.image});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: ${(props: ImgContainerProps) => props.left}px 0px;
  width: ${(props: ImgContainerProps) => props.uWidth}px;
  height: 100%;
  position: absolute;
`

const Preview = styled.div`
  height: 100%;
  width: 100%;
`

const TryOnV1 = ({
  height,
  width,
  data,
  aspectRatio,
  frameWidth,
  slideFull,
  frontOnly
}: TryOnProps): ReactElement => {
  const [loading, setLoading] = useState(true)
  const [image, setImage] = useState(new Image())
  const [frames, setFrames] = useState<FrameProps[]>([])
  const [initialFrameIndex, setInitialFrameIndex] = useState(0)
  const imageContainerRef = createRef<HTMLDivElement>()
  const { usableHeight, usableWidth } = calculateUsableDimensions(height, width, aspectRatio)

  useEffect(() => {
    setLoading(true)
    const img = new Image()
    img.src = data
    img.onload = () => {
      setImage(img)
    }
  }, [data, frameWidth])

  useEffect(() => {
    if (image) {
      const totalFrames = image.width / frameWidth
      const availableFrames: FrameProps[] = [...Array(totalFrames)].map((_, i) => {
        return { left: -(i * usableWidth) }
      })
      const frameIndex = totalFrames === 1 ? 0 : (totalFrames - 1) / 2
      setInitialFrameIndex(frameIndex)
      setFrames(availableFrames)
      setLoading(false)
      window.dispatchEvent(new Event('resize'))
    }
  }, [image, usableWidth, frameWidth])

  const handleUnwantedDrag = (e: any): void => {
    e.preventDefault()
  }

  const handleSliderChange = (percentage: number): void => {
    const currentFrameIndex = Math.round((frames.length - 1) * (percentage / 100))

    if (frames[currentFrameIndex] && imageContainerRef.current) {
      imageContainerRef.current.style.backgroundPosition = `${frames[currentFrameIndex].left}px 0px`
    }
  }

  if (loading) {
    return (
      <Placeholder
        width={usableWidth}
        height={usableHeight}
        loading={true}
      >
        &nbsp;
      </Placeholder>
    )
  }

  return (
    <Wrapper uWidth={`${usableWidth}px`} uHeight={`${usableHeight}px`} className='topology-preview-wrapper'>
      {!frontOnly && (
        <HintAnimation></HintAnimation>
      )}
      {frames.length > 0 && (
        <Preview className='topology-preview'>
          <ImgContainer image={image.src}
                        left={frames[initialFrameIndex].left}
                        uWidth={`${usableWidth}`}
                        className='vto-image-container'
                        ref={imageContainerRef}
                        onDragStart={handleUnwantedDrag} />
            {!frontOnly && (
              <SVGSlider slideFull={slideFull}
                        usableWidth={usableWidth * 0.8}
                        className='topology-slider'
                        onChange={handleSliderChange} />
            )}
        </Preview>
      )}
    </Wrapper>
  )
}

export default TryOnV1
